import React, { useState } from 'react';
import { initials } from './InitialsAvatar';

export const ROLES = ['Client', 'Développeur', 'Administrateur', 'Chef de projet', 'Testeur', 'Observateur'];

const UserSelector = ({ selectedUsers = [], onChange, availableUsers = [] }) => {
  const [userSearch, setUserSearch] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  const filteredUsers = availableUsers.filter(user => 
    user.name.toLowerCase().includes(userSearch.toLowerCase()) && 
    !selectedUsers.some(u => u.id === user.id || u.userId === user.id) // Check both id formats
  );

  const handleSelectUser = (user) => {
    // Add user immediately with no roles
    const newUser = { ...user, roles: [] };
    // Normalize ID if needed, but let's keep original user object structure + roles
    onChange([newUser, ...selectedUsers]);
    setUserSearch('');
    setShowUserDropdown(false);
  };

  const removeUser = (userId) => {
    onChange(selectedUsers.filter(u => (u.id || u.userId) !== userId));
  };

  const handleRoleToggle = (userId, role) => {
    onChange(selectedUsers.map(user => {
      if ((user.id || user.userId) !== userId) return user;
      
      const currentRoles = user.roles || [];
      const newRoles = currentRoles.includes(role)
        ? currentRoles.filter(r => r !== role)
        : [...currentRoles, role];
        
      return { ...user, roles: newRoles };
    }));
  };

  return (
    <div className="space-y-6">
      {/* User Search & Selection */}
      <div className="relative">
        <label className="field-label mb-2">Ajouter un utilisateur</label>
        <div className="relative">
          <input 
            type="text" 
            placeholder="Rechercher un utilisateur..." 
            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-iris-500 focus:border-iris-500"
            value={userSearch}
            onChange={(e) => {
              setUserSearch(e.target.value);
              setShowUserDropdown(true);
            }}
            onFocus={() => setShowUserDropdown(true)}
            onBlur={() => setTimeout(() => setShowUserDropdown(false), 200)}
          />
          <svg className="w-5 h-5 text-slate-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          
          {/* Dropdown */}
          {showUserDropdown && userSearch && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {filteredUsers.length > 0 ? (
                filteredUsers.map(user => (
                  <div 
                    key={user.id} 
                    className="px-4 py-3 hover:bg-iris-50 cursor-pointer flex items-center gap-3 transition-colors"
                    onMouseDown={(e) => {
                        e.preventDefault(); // Prevent blur
                        handleSelectUser(user);
                    }}
                  >
                    <div className="w-8 h-8 rounded-full bg-iris-100 flex items-center justify-center text-iris-600 font-bold text-xs">
                      {initials(user.name)}
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">{user.name}</div>
                      <div className="text-xs text-slate-500">{user.email}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-4 py-3 text-sm text-slate-500 italic">Aucun utilisateur trouvé</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Users List */}
      <div>
        <h4 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4">Utilisateurs du projet ({selectedUsers.length})</h4>
        <div className="space-y-3">
          {selectedUsers.length > 0 ? (
            selectedUsers.map(user => (
              <div key={user.id || user.userId} className="flex flex-col p-4 bg-slate-50 rounded-lg border border-slate-100 hover:border-slate-200 transition-all">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-500 font-bold">
                      {user.name ? initials(user.name) : '?'}
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">{user.name}</div>
                      {user.email && <div className="text-xs text-slate-500">{user.email}</div>}
                    </div>
                  </div>
                  <button 
                    onClick={() => removeUser(user.id || user.userId)}
                    className="text-slate-400 hover:text-red-500 p-2 rounded-full hover:bg-red-50 transition-colors"
                    title="Retirer du projet"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  </button>
                </div>
                
                {/* Roles Selection */}
                <div className="pl-14">
                  <div className="text-xs font-medium text-slate-500 mb-2 uppercase tracking-wide">Rôles :</div>
                  <div className="flex flex-wrap gap-2">
                    {ROLES.map(role => (
                      <label key={role} className={`inline-flex items-center px-2.5 py-1.5 rounded-full text-xs font-medium border cursor-pointer transition-colors ${
                        (user.roles || []).includes(role)
                          ? 'bg-iris-100 text-iris-800 border-iris-200'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      }`}>
                        <input 
                          type="checkbox" 
                          className="sr-only"
                          checked={(user.roles || []).includes(role)}
                          onChange={() => handleRoleToggle(user.id || user.userId, role)}
                        />
                        {role}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-slate-400 bg-slate-50 rounded-lg border border-dashed border-slate-300">
              Aucun utilisateur assigné à ce projet
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserSelector;
